import { waitForLimitedTimeOffer } from '@/components/ShowModal/limitedTimeOffer'
import React, { useEffect, useState } from 'react'
import GradientSpinner from '@/components/GradientSpinner/GradientSpinner'
import { KIND } from 'baseui/button'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@/store/store'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useEditorContext } from '@/scenes/engine'
import { customAmplitude } from '@/utils/customAmplitude'
import { selectUserIsPremium } from '@/store/slices/user/selectors'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { useDownloadConfetti } from '@/hooks/useDownloadConfetti'
import WrapPopover from '../WrapPopover'
import PopoverHeader from '../PopoverHeader'
import PopoverDropdown, { DropdownOption } from '../PopoverDropdown'
import PopoverRadioSelectionButtons from './PopoverRadioSelectionButtons'
import PopoverFullWidthButton from './PopoverFullWidthButton'
import Icons from '../../../../Icons'
import { lightTheme } from '@/customTheme'
import { PlacementType } from '../../PopoverPlacement/findEffectivePlacement'
import {
  FORMAT_OPTIONS as FORMAT_OPTIONS_DATA,
  VIDEO_FORMAT_OPTIONS,
  FormatOption,
  QUALITY_OPTION,
  QUALITY_MULTIPLIERS,
  QUALITY_OPTIONS,
  DEFAULT_FORMAT_KEY,
} from '@/scenes/Editor/components/Navbar/components/downloadTypes'
import {
  convertImagesToFormat,
  reportImageDownload,
  saveImages,
} from '@/services/download/imageExporter'


// ─── Constants ────────────────────────────────────────────────────────────────

const FORMAT_DROPDOWN_OPTIONS: DropdownOption[] = Object.entries(FORMAT_OPTIONS_DATA).map(([key, opt]) => ({
  id: opt.id,
  value: key,
  label: opt.title.toUpperCase(),
  description: opt.desc,
  isPremium: !!opt.isPremium,
}))

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DownloadPopoverProps {
  onClose: () => void
  buttonRef?: React.RefObject<HTMLElement>
  placement?: PlacementType
  /** 'all' downloads every page, 'current' downloads the active page, pass an array of scene IDs to download specific pages, or pass a function that returns pre-rendered image strings (e.g. base64 data-URLs) to download directly.
   * When a function is used, the returned images are used as the source content, but the final exported file format for non-PDF downloads follows the format selected in the UI (for example, PNG or JPG), not the mime type of the returned images. If the user selects PDF, the returned images are embedded into a PDF. Default: 'all' */
  downloadScope?: 'all' | 'current' | string[] | (() => string[] | Promise<string[]>)
  /** When false, renders content in a plain div (for use inside StatefulPopover). Default: true */
  usePanel?: boolean
  source: string
  destination: string
  /** Show the Photo Format dropdown. Default: true */
  hasImages?: boolean
  /** Show the disabled Video Format (MP4) dropdown. Default: false */
  hasVideos?: boolean
  /**
   * Self-contained save handler. When provided, replaces the default save step in all
   * paths (editor and canvas-less). Receives the chosen FormatOption and quality multiplier;
   * the callee owns fetch, conversion, analytics, and save.
   */
  onSave?: (format: FormatOption, quality: number) => Promise<void>
  defaultFormatKey?: string
  showWatermarkButton?: boolean
  /** Show a spinner on the Download button while the export is in progress. Default: false */
  showDownloadLoader?: boolean
  /** When provided, free users clicking Download see a premium prompt with this source string instead of downloading. */
  downloadButtonOpenPremiumPrompt?: string
}

// ─── Component ────────────────────────────────────────────────────────────────

function DownloadPopover({
  onClose,
  buttonRef,
  placement = 'bottom',
  downloadScope = 'all',
  usePanel = true,
  source,
  destination,
  onSave,
  hasImages = true,
  hasVideos = false,
  defaultFormatKey = DEFAULT_FORMAT_KEY,
  showWatermarkButton = true,
  showDownloadLoader = false,
  downloadButtonOpenPremiumPrompt,
}: DownloadPopoverProps) {
  const { editor } = useEditorContext()
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const hasPremium = useSelector(selectUserIsPremium)
  const triggerConfetti = useDownloadConfetti()

  const [formatValue, setFormatValue] = useState<FormatOption>(
    FORMAT_OPTIONS_DATA[defaultFormatKey] ?? FORMAT_OPTIONS_DATA[DEFAULT_FORMAT_KEY],
  )
  const [qualityOption, setQualityOption] = useState<QUALITY_OPTION>(QUALITY_OPTION.LOW)
  const [qualityInitialized, setQualityInitialized] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const SHOW_QUALITY_OPTIONS = false

  // Refresh the default format whenever the editor, download scope, premium status,
  // frame transparency, or scene count changes.
  // When downloadScope is a function the caller controls scene resolution; treat as 'all' for format inference.
  const resolvedScope: 'all' | 'current' =
    typeof downloadScope === 'function'
      ? 'all'
      : Array.isArray(downloadScope)
        ? (downloadScope.length === 1 ? 'current' : 'all')
        : downloadScope

  useEffect(() => {
    if (!editor) return
    const refresh = () => setFormatValue(editor.handlers.downloadHandler.getDefaultFormat(resolvedScope))
    refresh()
    editor.on('frame:transparent', refresh)
    editor.on('scenes:changed', refresh)
    return () => {
      editor.off('frame:transparent', refresh)
      editor.off('scenes:changed', refresh)
    }
  }, [editor, resolvedScope, hasPremium])

  // Initialise quality based on premium status
  useEffect(() => {
    if (qualityInitialized) return
    if (hasPremium) setQualityOption(QUALITY_OPTION.HIGH)
    setQualityInitialized(true)
  }, [hasPremium, qualityInitialized])

  // ─── Download helpers ──────────────────────────────────────────────────────

  const getQualityMultiplier = () => QUALITY_MULTIPLIERS[qualityOption]

  const handleDownload = async () => {
    const quality = getQualityMultiplier()

    // Editor-coupled path: scenes, current scene, scene IDs, or callback scope
    // when the editor is mounted (e.g. AI Fashion Download all).
    if (editor) {
      if (onSave) {
        // Self-contained path: caller owns fetch + convert + analytics + save.
        await onSave(formatValue, quality)
      } else {
        await editor.handlers.downloadHandler.download({
          scope: downloadScope,
          format: formatValue,
          quality,
          qualityOption,
          routeId: id,
          source,
          destination,
        })
      }
      return
    }

    // Canvas-less path (e.g. AI Studio, Photoshoots dashboard route).
    if (onSave) {
      await onSave(formatValue, quality)
      return
    }

    // Callback scope: the only remaining valid path is a function that returns
    // pre-rendered data-URL images (e.g. Photoshoots dashboard route).
    if (typeof downloadScope !== 'function' || !(await waitForLimitedTimeOffer())) return
    const rawImages = await downloadScope()
    if (rawImages.length === 0) return
    const images =
      formatValue.title === 'pdf'
        ? rawImages
        : await convertImagesToFormat(rawImages, formatValue.title, quality)
    reportImageDownload({
      formatTitle: formatValue.title,
      qualityOption,
      source,
      destination,
      numberOfPages: rawImages.length,
    })
    await saveImages(images, formatValue)
  }

  // ─── Premium handlers ──────────────────────────────────────────────────────

  const handlePremiumFormatClick = (value: string) => {
    const source = value === 'pdf' ? 'BtSubscriptionPDF' : 'BtSubscriptionWebp'
    dispatch(setOpenModalTryPremium({ isOpen: true, source, callback: () => setFormatValue(FORMAT_OPTIONS_DATA[value]) }))
    customAmplitude('Premium Prompt', { Source: source, Type: 'Standard' })
  }

  const handlePremiumQualityClick = () => {
    dispatch(setOpenModalTryPremium({
      isOpen: true,
      source: 'BtSubscriptionHighQuality',
      callback: () => setQualityOption(QUALITY_OPTION.HIGH),
    }))
    customAmplitude('Premium Prompt', { Source: 'BtSubscriptionHighQuality', Type: 'Standard' })
  }

  const handleWatermarkClick = () => {
    dispatch(setOpenModalTryPremium({ isOpen: true, source: 'BtSubscriptionWatermark', callback: () => {} }))
    customAmplitude('Premium Prompt', { Source: 'BtSubscriptionWatermark' })
  }

  // ─── Dropdown options (mark premium only when user doesn't have it) ────────

  const formatDropdownOptions = FORMAT_DROPDOWN_OPTIONS.map(opt => ({
    ...opt,
    isPremium: opt.isPremium && !hasPremium,
  }))

  // ─── Inner content ────────────────────────────────────────────────────────

  const innerContent = (
    <>
      {hasImages && (
        <PopoverDropdown
          label={t('Photo Format')}
          options={formatDropdownOptions}
          selectedValue={formatValue.title}
          onChange={value => setFormatValue(FORMAT_OPTIONS_DATA[value])}
          onPremiumClick={handlePremiumFormatClick}
          width={292}
        />
      )}
      {hasVideos && (
        <PopoverDropdown
          label={t('Video Format')}
          options={Object.entries(VIDEO_FORMAT_OPTIONS).map(([key, opt]) => ({
            id: opt.id,
            value: key,
            label: opt.title.toUpperCase(),
            description: opt.desc,
          }))}
          selectedValue="mp4"
          onChange={() => {}}
          width={292}
        />
      )}
      {SHOW_QUALITY_OPTIONS && (
        <PopoverRadioSelectionButtons
          label={t('Quality')}
          options={QUALITY_OPTIONS.map(opt => ({ ...opt, label: t(opt.label) }))}
          selectedValue={qualityOption}
          onClick={value => setQualityOption(value as QUALITY_OPTION)}
          onPremiumClick={handlePremiumQualityClick}
        />
      )}
      {showWatermarkButton && !hasPremium && (
        <PopoverFullWidthButton
          kind={KIND.secondary}
          label={t('Remove watermark')}
          onClick={handleWatermarkClick}
          isPremium
        />
      )}
      <PopoverFullWidthButton
        kind={KIND.primary}
        label={t('Download')}
        icon={showDownloadLoader && isDownloading ? <GradientSpinner fill="white" size={24} style={{ flex: 'none' }} /> : <Icons.Download />}
        onClick={async () => {
          if (downloadButtonOpenPremiumPrompt && !hasPremium) {
            dispatch(setOpenModalTryPremium({
              isOpen: true,
              source: downloadButtonOpenPremiumPrompt,
              callback: () => {
                setIsDownloading(true)
                triggerConfetti()
                handleDownload().then(() => onClose())
              },
              opened: true,
            }))
            customAmplitude('Premium Prompt', { Source: downloadButtonOpenPremiumPrompt, Type: 'Standard' })
            return
          }
          setIsDownloading(true)
          triggerConfetti()
          await handleDownload()
          onClose()
        }}
      />
    </>
  )

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!usePanel) {
    return (
      <div
        style={{
          width: 320,
          background: lightTheme.colors.white,
          borderRadius: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          padding: '12px 16px 16px',
          overflow: 'visible',
        }}
      >
        <PopoverHeader title={t('Download')} showClose onClose={onClose} />
        {innerContent}
      </div>
    )
  }

  return (
    <WrapPopover
      width={320}
      title={t('Download')}
      showCloseButton
      onClose={onClose}
      gap={16}
      padding="12,16,16,16"
      placement={placement}
      buttonRef={buttonRef}
    >
      {innerContent}
    </WrapPopover>
  )
}

export default DownloadPopover
